import { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    DropdownAtom,
    ModalAtom,
    TextFieldAtom,
} from "../../../components/atoms/atom";
import "./add-company-modal.scss";

const COUNTRIES = [
    { value: "Germany", label: "Germany" },
    { value: "Austria", label: "Austria" },
    { value: "Switzerland", label: "Switzerland" },
    { value: "United States", label: "United States" },
    { value: "United Kingdom", label: "United Kingdom" },
    { value: "France", label: "France" },
    { value: "Italy", label: "Italy" },
    { value: "Spain", label: "Spain" },
    { value: "Netherlands", label: "Netherlands" },
    { value: "Belgium", label: "Belgium" },
];

const SUGGEST_MIN_LENGTH = 2;
const SUGGEST_LIMIT = 8;

const emptyFormData = {
    name: "",
    addressStreet: "",
    addressZipCode: "",
    addressCity: "",
    addressCountry: "",
    phoneNumber: "",
    taxId: "",
};

const formatCompanyAddress = (companyRow) => {
    const parts = [
        companyRow.addressStreet,
        companyRow.addressZipCode,
        companyRow.addressCity,
        companyRow.addressCountry,
    ].filter(Boolean);
    return parts.join(", ");
};

function AddCompanyModal({
    show,
    onHide,
    onSubmit,
    loading = false,
    initialData = null,
    title = null,
    existingCompanies = [],
    onSelectExisting,
}) {
    const { t } = useTranslation();

    // Typeahead / duplicate handling only applies when creating a new company.
    const isAddMode = !initialData;

    const [formData, setFormData] = useState(emptyFormData);
    const [errors, setErrors] = useState({});
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    // Inline same-name confirmation: null | { match }
    const [dupWarning, setDupWarning] = useState(null);

    const suggestRef = useRef(null);
    const skipSuggestRef = useRef(false);

    useEffect(() => {
        if (show) {
            if (initialData) {
                setFormData({
                    name: initialData.name ?? "",
                    addressStreet: initialData.addressStreet ?? "",
                    addressZipCode: initialData.addressZipCode ?? "",
                    addressCity: initialData.addressCity ?? "",
                    addressCountry: initialData.addressCountry ?? "",
                    phoneNumber: initialData.phoneNumber ?? "",
                    taxId: initialData.taxId ?? "",
                });
            } else {
                setFormData(emptyFormData);
            }
            setErrors({});
            setShowSuggestions(false);
            setHighlightIndex(-1);
            setDupWarning(null);
        }
    }, [show, initialData]);

    useEffect(() => {
        const onDocClick = (event) => {
            if (
                suggestRef.current &&
                !suggestRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
                setHighlightIndex(-1);
            }
        };
        document.addEventListener("mousedown", onDocClick);
        return () => document.removeEventListener("mousedown", onDocClick);
    }, []);

    // Case-insensitive name matches from the already-loaded master list.
    const suggestions = useMemo(() => {
        if (!isAddMode) return [];
        const query = formData.name.trim().toLowerCase();
        if (query.length < SUGGEST_MIN_LENGTH) return [];
        return existingCompanies
            .filter((c) => String(c.name ?? "").toLowerCase().includes(query))
            .slice(0, SUGGEST_LIMIT);
    }, [isAddMode, formData.name, existingCompanies]);

    const findExactMatch = (name) => {
        const lower = name.trim().toLowerCase();
        return (
            existingCompanies.find(
                (c) => String(c.name ?? "").trim().toLowerCase() === lower,
            ) || null
        );
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Clear error for this field
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const onNameChange = (e) => {
        const value = e.target.value;
        handleInputChange("name", value);
        // The name drives the duplicate check, so a fresh name invalidates any
        // pending same-name confirmation.
        setDupWarning(null);

        if (skipSuggestRef.current) {
            skipSuggestRef.current = false;
            return;
        }
        const trimmed = value.trim();
        if (trimmed.length < SUGGEST_MIN_LENGTH) {
            setShowSuggestions(false);
            setHighlightIndex(-1);
            return;
        }
        setShowSuggestions(true);
        setHighlightIndex(-1);
    };

    const selectSuggestion = (companyRow) => {
        // The master equivalent of "use the existing one" is to open it for
        // editing rather than creating a duplicate.
        skipSuggestRef.current = true;
        setShowSuggestions(false);
        setHighlightIndex(-1);
        setDupWarning(null);
        onSelectExisting?.(companyRow);
    };

    const onNameKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightIndex((prev) =>
                prev < suggestions.length - 1 ? prev + 1 : 0,
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIndex((prev) =>
                prev > 0 ? prev - 1 : suggestions.length - 1,
            );
        } else if (e.key === "Enter" && highlightIndex >= 0) {
            e.preventDefault();
            selectSuggestion(suggestions[highlightIndex]);
        } else if (e.key === "Escape") {
            setShowSuggestions(false);
            setHighlightIndex(-1);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "companies.error-name-required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const buildSubmitData = () => {
        if (initialData) {
            // Edit: send all fields so clearing a value persists (empty string is saved)
            return {
                name: formData.name.trim(),
                addressStreet: (formData.addressStreet ?? "").trim(),
                addressZipCode: (formData.addressZipCode ?? "").trim(),
                addressCity: (formData.addressCity ?? "").trim(),
                addressCountry: (formData.addressCountry ?? "").trim(),
                phoneNumber: (formData.phoneNumber ?? "").trim(),
                taxId: (formData.taxId ?? "").trim(),
                id: initialData.id,
            };
        }
        // Add: only include non-empty optional fields
        return Object.entries(formData).reduce((acc, [key, value]) => {
            if (value && value.trim()) {
                acc[key] = value.trim();
            }
            return acc;
        }, {});
    };

    // Create after the same-name confirmation (or when there is no collision).
    // allowDuplicateName tells the server to skip its duplicate guard.
    const submitCreate = (allowDuplicateName) => {
        setDupWarning(null);
        onSubmit(buildSubmitData(), allowDuplicateName);
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }
        // Edit keeps its original behaviour (no duplicate prompt).
        if (!isAddMode) {
            onSubmit(buildSubmitData());
            return;
        }
        const match = findExactMatch(formData.name);
        if (match) {
            setShowSuggestions(false);
            setDupWarning({ match });
            return;
        }
        submitCreate(false);
    };

    const handleClose = () => {
        setFormData(emptyFormData);
        setErrors({});
        setShowSuggestions(false);
        setHighlightIndex(-1);
        setDupWarning(null);
        onHide();
    };

    const showSuggestList =
        isAddMode &&
        showSuggestions &&
        formData.name.trim().length >= SUGGEST_MIN_LENGTH &&
        suggestions.length > 0;

    const modalBody = (
        <Container>
            <Row className="mb-3">
                <Col>
                    <div className="acm-name-suggest" ref={suggestRef}>
                        <TextFieldAtom
                            type="text"
                            label={t("companies.company-name")}
                            placeholder={
                                isAddMode
                                    ? t(
                                          "companies.company-name-search-placeholder",
                                      )
                                    : t("companies.company-name")
                            }
                            required
                            value={formData.name}
                            error={errors.name ? t(errors.name) : ""}
                            onChange={
                                isAddMode
                                    ? onNameChange
                                    : (e) =>
                                          handleInputChange(
                                              "name",
                                              e.target.value,
                                          )
                            }
                            onFocus={
                                isAddMode
                                    ? () => {
                                          if (
                                              formData.name.trim().length >=
                                              SUGGEST_MIN_LENGTH
                                          ) {
                                              setShowSuggestions(true);
                                          }
                                      }
                                    : undefined
                            }
                            onKeyDown={isAddMode ? onNameKeyDown : undefined}
                            autoComplete={isAddMode ? "off" : undefined}
                            disabled={loading}
                            maxLength={255}
                        />
                        {showSuggestList && (
                            <ul className="acm-suggest-list" role="listbox">
                                {suggestions.map((companyRow, index) => {
                                    const address =
                                        formatCompanyAddress(companyRow);
                                    return (
                                        <li
                                            key={companyRow.id}
                                            role="option"
                                            aria-selected={
                                                index === highlightIndex
                                            }
                                            className={
                                                index === highlightIndex
                                                    ? "active"
                                                    : ""
                                            }
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                selectSuggestion(companyRow);
                                            }}
                                        >
                                            <span className="acm-suggest-name">
                                                {companyRow.name}
                                            </span>
                                            {address && (
                                                <span className="acm-suggest-meta">
                                                    {address}
                                                </span>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                    {showSuggestList && (
                        <p className="acm-suggest-hint text-muted">
                            {t("companies.company-suggest-hint")}
                        </p>
                    )}
                </Col>
            </Row>

            {dupWarning && (
                <div className="acm-dup-warning" role="alert">
                    <div className="acm-dup-warning__text">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        {t("companies.duplicate-warning", {
                            name: formData.name.trim(),
                        })}
                        {formatCompanyAddress(dupWarning.match) && (
                            <span className="acm-dup-warning__addr">
                                {formatCompanyAddress(dupWarning.match)}
                            </span>
                        )}
                    </div>
                    <div className="acm-dup-warning__actions">
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => selectSuggestion(dupWarning.match)}
                        >
                            {t("companies.duplicate-edit-existing")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => submitCreate(true)}
                        >
                            {t("companies.duplicate-create-separate")}
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-link"
                            onClick={() => setDupWarning(null)}
                        >
                            {t("common.cancel")}
                        </button>
                    </div>
                </div>
            )}

            <Row className="mb-3">
                <Col>
                    <TextFieldAtom
                        type="text"
                        label={t("companies.street-house-number")}
                        placeholder={t("companies.street-house-number")}
                        value={formData.addressStreet}
                        error={errors.addressStreet ? t(errors.addressStreet) : ""}
                        onChange={(e) =>
                            handleInputChange("addressStreet", e.target.value)
                        }
                        disabled={loading}
                        maxLength={255}
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={4}>
                    <TextFieldAtom
                        type="text"
                        label={t("companies.postal-code")}
                        placeholder={t("companies.postal-code")}
                        value={formData.addressZipCode}
                        error={errors.addressZipCode ? t(errors.addressZipCode) : ""}
                        onChange={(e) =>
                            handleInputChange("addressZipCode", e.target.value)
                        }
                        disabled={loading}
                        maxLength={20}
                    />
                </Col>
                <Col md={4}>
                    <TextFieldAtom
                        type="text"
                        label={t("companies.city")}
                        placeholder={t("companies.city")}
                        value={formData.addressCity}
                        error={errors.addressCity ? t(errors.addressCity) : ""}
                        onChange={(e) =>
                            handleInputChange("addressCity", e.target.value)
                        }
                        disabled={loading}
                        maxLength={100}
                    />
                </Col>
                <Col md={4}>
                    <DropdownAtom
                        label={t("companies.country")}
                        placeholder={t("companies.country")}
                        options={COUNTRIES}
                        value={formData.addressCountry}
                        error={errors.addressCountry ? t(errors.addressCountry) : ""}
                        onChange={(e) =>
                            handleInputChange("addressCountry", e.target.value)
                        }
                        disabled={loading}
                        sortOptions={false}
                        isClearable
                    />
                </Col>
            </Row>

            <Row className="mb-3">
                <Col md={6}>
                    <TextFieldAtom
                        type="text"
                        label={t("companies.phone-number")}
                        placeholder={t("companies.phone-number")}
                        value={formData.phoneNumber}
                        error={errors.phoneNumber ? t(errors.phoneNumber) : ""}
                        onChange={(e) =>
                            handleInputChange("phoneNumber", e.target.value)
                        }
                        disabled={loading}
                        maxLength={20}
                    />
                </Col>
                <Col md={6}>
                    <TextFieldAtom
                        type="text"
                        label={t("companies.tax-id")}
                        placeholder={t("companies.tax-id")}
                        value={formData.taxId}
                        error={errors.taxId ? t(errors.taxId) : ""}
                        onChange={(e) => handleInputChange("taxId", e.target.value)}
                        disabled={loading}
                        maxLength={50}
                    />
                </Col>
            </Row>
        </Container>
    );

    const modalTitle = title ?? t("companies.add-company");
    return (
        <ModalAtom
            title={modalTitle}
            body={modalBody}
            show={show}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            saveText={loading ? t("common.loading") : t("common.save")}
            disabled={loading}
            submitDisabled={!!dupWarning}
            customClassName="settings-add-company-modal"
            size="lg"
        />
    );
}

export default AddCompanyModal;
